import { XMLBuilder } from 'fast-xml-parser';
import JSZip from 'jszip';
import _ from 'lodash';

import { PostData } from './model/PostTypes';
import { LanguageType, Translations } from '@lifeserver/shared';

import {
    CreatePortalLocalizationDTO,
    PortalLocalizationDTO,
    PortalLocalizationMetaDataDTO,
    PortalPostLocalizationDTO,
} from '../hooks/portal';

import { isIndexable } from '../types/indexable';

export type Path = Array<string | number>;

/**
 * Flattens Google Sheet translation tables into localization rows ready to
 * persist. For each requested key that exists in the sheet, emits one row per
 * language.
 *
 * Shared by the post translation flow (`PortalTranslationManager`) and the
 * eventsCalendar category flow (`CategoryModal`) so the sheet → localization
 * conversion lives in a single place.
 *
 * @param skipEmpty when true, values are trimmed and empty cells produce no
 * row — used when importing a single key for a category. Posts keep every row
 * verbatim, so it defaults to false.
 */
export function sheetTranslationsToLocalizations(
    keys: string[],
    sheet: Translations,
    { skipEmpty = false }: { skipEmpty?: boolean } = {},
): CreatePortalLocalizationDTO[] {
    const rows: CreatePortalLocalizationDTO[] = [];

    for (const key of keys) {
        const table = sheet[key];
        if (!table) {
            continue;
        }
        for (const [language, value] of Object.entries(table)) {
            const translation = skipEmpty ? (value?.trim() ?? '') : value;
            if (skipEmpty && translation.length === 0) {
                continue;
            }
            rows.push({ id: key, language, translation });
        }
    }

    return rows;
}

export function getTranslatableFields(payload: object): Array<Path> {
    const paths: Array<Path> = [];

    if (!isIndexable(payload)) {
        return paths;
    }

    for (const keyRaw in payload) {
        const key = Array.isArray(payload) ? Number(keyRaw) : keyRaw;
        const value = Array.isArray(payload)
            ? payload[Number(key)]
            : payload[key];

        if (
            value !== null &&
            typeof value === 'object' &&
            '$type' in value &&
            value.$type === 'TranslationTable'
        ) {
            paths.push([key]);
        } else if (isIndexable(value)) {
            paths.push(
                ...getTranslatableFields(value).map((path) => [key, ...path]),
            );
        }
    }

    return paths;
}

export function getValidTranslationPaths(payload: object): Set<string> {
    return new Set(
        getTranslatableFields(payload).map((path) => path.join('.')),
    );
}

export function pruneStalePostLocalizations<
    T extends { path: string; portalLocalizationId?: string },
>(postLocalizations: T[], payload: object): T[] {
    const validPaths = getValidTranslationPaths(payload);
    return postLocalizations.filter((loc) => validPaths.has(loc.path));
}

export function pruneOrphanedLocalizations<T extends { id: string }>(
    localizations: T[],
    referencedIds: Set<string>,
): T[] {
    return localizations.filter((loc) => referencedIds.has(loc.id));
}

export function getTranslationKey(prefix: string, path?: Path): string {
    const normalizedPrefix = prefix.replaceAll(/\s/g, '_');
    if (path && path.length > 0) {
        return `${normalizedPrefix}.${path.join('.')}`;
    }
    return normalizedPrefix;
}

export function getTranslationState(
    prefix: string,
    translatableFields: Array<Path>,
    payload: object,
    translations: Translations,
    localizations: Record<string, Record<string, string>>,
) {
    const hasNewTranslations = translatableFields.some((path) => {
        const translationTable = _.get(payload, path);
        const manualKey = translationTable?.$key;
        const translationKey = manualKey ?? getTranslationKey(prefix, path);

        const localizationsTranslations = localizations?.[translationKey] ?? {};
        const latest = translations[translationKey];

        if (!latest) return false;
        return !_.isEqual(latest, localizationsTranslations);
    });

    const ALL_LANGUAGES = Object.values(LanguageType);
    const missingTranslations = translatableFields
        .map((path) => {
            const translationTable = _.get(payload, path);
            const manualKey = translationTable?.$key;
            const translationKey = manualKey ?? getTranslationKey(prefix, path);

            const locTranslations = localizations?.[translationKey] ?? {};

            const missingLanguages = ALL_LANGUAGES.filter(
                (language) =>
                    !locTranslations[language] ||
                    locTranslations[language].trim() === '',
            );

            return {
                translationKey,
                missingLanguages,
                hasKey: Boolean(manualKey),
            };
        })
        .filter(
            ({ translationKey, missingLanguages, hasKey }) =>
                missingLanguages.length > 0 &&
                // Suppress only when ALL languages are missing and the field is unconfigured
                (missingLanguages.length !== ALL_LANGUAGES.length ||
                    hasKey ||
                    localizations?.[translationKey] !== undefined),
        );

    return {
        hasNewTranslations,
        missingTranslations,
    };
}

export function applyTranslations<T extends object>(
    prefix: string,
    translatableFields: Array<Path>,
    payload: T,
): T {
    const newPayload = _.cloneDeep(payload);

    for (const path of translatableFields) {
        const translationTable = _.get(newPayload, path);
        const manualKey = translationTable?.$key;
        const translationKey = manualKey ?? getTranslationKey(prefix, path);

        _.set(newPayload, path, {
            $type: 'TranslationTable',
            $key: translationKey,
        });
    }

    return newPayload;
}

const TOKEN_ENTRIES = [
    { token: /^\.?pages\.(\d)/, name: 'Page {0}' },
    { token: /^\.?body\.text/, name: 'Body' },
    { token: /^\.?header(?:\.text)?/, name: 'Header' },
    { token: /^\.?button\.text/, name: 'Button' },
    { token: /^\.?popTexts\.(\d)/, name: 'PopText {0}' },
    { token: /^\.?rows\.(\d)\.text/, name: 'Row {0}' },
];

function getTokenMatch(text: string) {
    for (const tokenEntry of TOKEN_ENTRIES) {
        const match = tokenEntry.token.exec(text);
        if (match) {
            return { tokenEntry, match };
        }
    }
    return null;
}
export function pathToFieldName(path: Path): string {
    let pathStr = path
        .map((part) => {
            // Convert 0-based index to 1-based
            if (typeof part === 'number') {
                return part + 1;
            }
            return part;
        })
        .join('.');
    const fieldName: string[] = [];

    while (pathStr) {
        const tokenMatch = getTokenMatch(pathStr);
        if (!tokenMatch) {
            // Couldn't convert the whole string to a display name,
            // bailing out with just path
            return path.join('.');
        }

        pathStr = pathStr.replace(tokenMatch.tokenEntry.token, '');

        let fieldNamePart = tokenMatch.tokenEntry.name;
        tokenMatch.match.slice(1).forEach((captureGroup, i) => {
            fieldNamePart = fieldNamePart.replace(`{${i}}`, captureGroup);
        });
        fieldName.push(fieldNamePart);
    }

    return fieldName.join(' ⟶ ');
}

export function getTranslation(
    localizations: Record<string, Record<string, string>>,
    id: string,
    language: string,
) {
    return localizations[id]?.[language] ?? '';
}
export const createTextKey = (pathPrefix: (string | number)[]) => {
    return `${pathPrefix.join('.')}`;
};
export function localizationArrayToRecord(
    arr: PortalLocalizationDTO[],
): Record<string, Record<string, string>> {
    const map: Record<string, Record<string, string>> = {};
    for (const loc of arr) {
        if (!map[loc.id]) map[loc.id] = {};
        map[loc.id][loc.language] = loc.translation;
    }
    return map;
}

function createXliff(
    localizations: PortalLocalizationMetaDataDTO[],
    targetLanguage: string,
) {
    const transUnits = localizations.map((group) => {
        const id = group.localizations[0]?.id;
        const translations: Record<string, string> = {};
        group.localizations.forEach((loc) => {
            translations[loc.language] = loc.translation;
        });
        const notes = [];
        if (group?.metadata?.characterLimit) {
            notes.push(`Character Limit: ${group.metadata.characterLimit}`);
        }
        if (group?.metadata?.notes) {
            notes.push(`Notes: ${group.metadata.notes}`);
        }

        return {
            'trans-unit': {
                '@_id': id,
                source: translations['en'] || '',
                target: translations[targetLanguage] || '',
                note: notes.join(' | '),
            },
        };
    });

    const xliffObj = {
        xliff: {
            '@_xmlns:xsd': 'http://www.w3.org/2001/XMLSchema',
            '@_xmlns:xsi': 'http://www.w3.org/2001/XMLSchema-instance',
            '@_xmlns': 'urn:oasis:names:tc:xliff:document:1.2',
            '@_version': '1.2',
            file: {
                '@_source-language': 'en',
                '@_target-language': targetLanguage,
                '@_datatype': 'plaintext',
                '@_original': 'file',
                body: transUnits,
            },
        },
    };

    const builder = new XMLBuilder({
        format: true,
        ignoreAttributes: false,
        indentBy: '  ',
        suppressEmptyNode: false,
    });

    const xmlString = `<?xml version="1.0" encoding="UTF-8"?>\n${builder.build(xliffObj)}`;
    return xmlString;
}

export async function downloadAllXliffAsZip(
    localizations: PortalLocalizationMetaDataDTO[],
    allLanguages: string[],
) {
    const zip = new JSZip();
    for (const lang of allLanguages) {
        const xliff = createXliff(localizations, lang);
        zip.file(`export_${lang}.xliff`, xliff);
    }
    const blob = await zip.generateAsync({ type: 'blob' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'all_translations.zip';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
}

export function parseXliff(xliffString: string) {
    const parser = new DOMParser();
    const xml = parser.parseFromString(xliffString, 'application/xml');
    const transUnits = Array.from(xml.getElementsByTagName('trans-unit'));
    const fileTag = xml.getElementsByTagName('file')[0];
    const targetLanguage = fileTag?.getAttribute('target-language');

    const result: {
        id: string;
        language: string;
        translation: string;
    }[] = [];

    if (targetLanguage === null) {
        throw new Error('Target language not specified in XLIFF file.');
    }
    for (const unit of transUnits) {
        const id = unit.getAttribute('id') || '';
        const source =
            unit.getElementsByTagName('source')[0]?.textContent || '';
        const target =
            unit.getElementsByTagName('target')[0]?.textContent || '';

        result.push({
            id,
            language: 'en',
            translation: source,
        });
        result.push({
            id,
            language: targetLanguage,
            translation: target,
        });
    }

    return result;
}

export function setOrUnsetKeyInPayload(
    newPayload: PostData,
    field: Path,
    newKey: string | undefined,
) {
    const manualKeyPath = [...field, '$key'];
    const typePath = [...field, '$type'];
    if (newKey) {
        _.set(newPayload, manualKeyPath, newKey);
        _.set(newPayload, typePath, 'TranslationTable');
    } else {
        _.unset(newPayload, manualKeyPath);
        _.unset(newPayload, typePath);
    }
}
export function updatePostLocalizationsArray(
    path: string,
    newKey: string,
    postLocalizations: PortalPostLocalizationDTO[],
    postId: string,
) {
    const existsAtPath = postLocalizations.some((loc) => loc.path === path);
    let updated = postLocalizations.map((loc) =>
        loc.path === path ? { ...loc, portalLocalizationId: newKey } : loc,
    );
    if (newKey && !existsAtPath) {
        updated = [
            ...updated,
            {
                portalLocalizationId: newKey,
                portalPostId: postId,
                path,
            },
        ];
    }
    return updated;
}

export function updateLocalizationsAndPayload(
    field: Path,
    newKey: string | undefined,
    localizations: PortalLocalizationDTO[],
    updatedLocalizations: PortalLocalizationDTO[],
    payload: PostData,
    translationsRaw: Record<string, Record<string, string>>,
) {
    if (newKey && translationsRaw?.[newKey]) {
        const sheetLanguages = Object.keys(translationsRaw[newKey]);
        sheetLanguages.forEach((lang) => {
            const existsLoc = updatedLocalizations.some(
                (loc) => loc.id === newKey && loc.language === lang,
            );
            if (!existsLoc) {
                updatedLocalizations.push({
                    id: newKey,
                    language: lang,
                    translation: translationsRaw[newKey][lang] ?? '',
                    metadataId: null,
                    metadata: undefined,
                });
            }

            const localizationTranslation =
                localizations.find(
                    (loc) => loc.id === newKey && loc.language === lang,
                )?.translation ??
                translationsRaw[newKey][lang] ??
                '';
            _.set(payload, [...field, lang], localizationTranslation);
        });
    }
}
function clearTranslationsValuesFromPayload(
    payload: PostData,
    field: Path,
    allLanguages: string[],
) {
    allLanguages.forEach((lang) => {
        _.unset(payload, [...field, lang]);
    });
}
export function initializeNewTranslationKey(
    newPayload: PostData,
    field: Path,
    newKey: string,
    allLanguages: string[],
    updatedLocalizations: PortalLocalizationDTO[],
    updatedPostLocalizations: PortalPostLocalizationDTO[],
    path: string,
    postId: string,
) {
    clearTranslationsValuesFromPayload(newPayload, field, allLanguages);
    allLanguages.forEach((lang) => {
        const existsLoc = updatedLocalizations.some(
            (loc) => loc.id === newKey && loc.language === lang,
        );
        if (!existsLoc) {
            updatedLocalizations.push({
                id: newKey,
                language: lang,
                translation: '',
                metadataId: null,
                metadata: undefined,
            });
        }
    });
    const newPostLocalizations = updatePostLocalizationsArray(
        path,
        newKey,
        updatedPostLocalizations,
        postId,
    );

    return {
        updatedLocalizations,
        updatedPostLocalizations: newPostLocalizations,
    };
}
